import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import PageSEO from "@/components/PageSEO";
import SiteFooter from "@/components/SiteFooter";
import MarketTicker from "@/components/MarketTicker";
import ArticleCard from "@/components/ArticleCard";
import AISearchDialog from "@/components/AISearchDialog";
import { useArticles } from "@/hooks/useArticles";
import { Button } from "@/components/ui/button";
import { Clock, TrendingUp, Search } from "lucide-react";
import { useTrendingArticles } from "@/hooks/useTrendingArticles";

const Index = () => {
  const [visibleCount, setVisibleCount] = useState(18);
  const [searchOpen, setSearchOpen] = useState(false);
  const { trendingSlugs } = useTrendingArticles(5);
  const { articles, heroArticles: dbHeroArticles, latestArticles: dbLatestArticles, trendingArticles: dbTrendingArticles } = useArticles(true);

  const heroPool = dbHeroArticles.length > 0
    ? dbHeroArticles
    : dbTrendingArticles.length > 0
      ? dbTrendingArticles
      : articles;
  const heroArticles = heroPool.slice(0, 3);

  const fallbackLatest = [...articles]
    .filter((a) => !heroArticles.some((h) => h.id === a.id))
    .sort((a, b) => new Date(b.publicationDate).getTime() - new Date(a.publicationDate).getTime())
    .slice(0, 5);
  const latestRibbon = dbLatestArticles.length > 0
    ? dbLatestArticles.filter((a) => !heroArticles.some((h) => h.id === a.id)).slice(0, 5)
    : fallbackLatest;

  let trendingRibbon: typeof articles;
  if (dbTrendingArticles.length > 0) {
    trendingRibbon = dbTrendingArticles.slice(0, 5);
  } else {
    const trendingFromViews = trendingSlugs
      .map((slug) => articles.find((a) => a.slug === slug))
      .filter(Boolean) as typeof articles;
    const fallbackTrending = articles
      .filter((a) => (a.category === "Markets & Trading" || a.category === "Oil, Gas & Commodities") && !trendingFromViews.some((t) => t.id === a.id))
      .slice(0, 5 - trendingFromViews.length);
    trendingRibbon = [...trendingFromViews, ...fallbackTrending].slice(0, 5);
  }

  const allCoverage = [...articles]
    .filter((a) => !heroArticles.some((h) => h.id === a.id))
    .sort((a, b) => new Date(b.publicationDate).getTime() - new Date(a.publicationDate).getTime());

  const handleShowMore = () => setVisibleCount((prev) => prev + 18);

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Global Energy Insider | Energy News & Markets"
        description="Breaking energy news, expert analysis on oil & gas, renewables, shipping, and commodities. Stay ahead of global energy market trends and policy shifts."
        canonicalPath="/"
      />
      <SiteHeader />
      <MarketTicker />

      <main>
        {/* Hero Section */}
        <section className="container pt-6 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 flex flex-col gap-5">
              {heroArticles[0] && (
                <ArticleCard article={heroArticles[0]} variant="featured" priority />
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {heroArticles.slice(1, 3).map((article) => (
                  <ArticleCard key={article.id} article={article} variant="featured" />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-4 py-3 rounded-lg bg-surface border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors w-full"
              >
                <Search className="w-4 h-4 text-primary" />
                <span>Search or Ask AI about energy markets...</span>
              </button>

              {/* Latest Ribbon */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-divider">
                  <Clock className="w-4 h-4 text-primary" />
                  <h3 className="font-display text-sm font-bold text-foreground uppercase tracking-wider">Latest</h3>
                </div>
                <div className="space-y-3">
                  {latestRibbon.map((article, i) => (
                    <Link key={article.id} to={`/article/${article.slug}`} className="block group">
                      <div className="flex gap-3 items-start">
                        <span className="text-xs font-bold text-primary/60 mt-0.5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">{article.title}</p>
                          <span className="text-xs text-muted-foreground mt-1 block">{article.publicationDate}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Trending Ribbon */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-divider">
                  <TrendingUp className="w-4 h-4 text-accent" />
                  <h3 className="font-display text-sm font-bold text-foreground uppercase tracking-wider">Trending</h3>
                </div>
                <div className="space-y-3">
                  {trendingRibbon.map((article, i) => (
                    <Link key={article.id} to={`/article/${article.slug}`} className="block group">
                      <div className="flex gap-3 items-start">
                        <span className="text-xs font-bold text-accent/60 mt-0.5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground leading-snug line-clamp-2 group-hover:text-accent transition-colors">{article.title}</p>
                          <span className="text-xs text-muted-foreground mt-1 block">{article.readTime} min read</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All Coverage */}
        <section className="container py-8">
          <div className="mb-6">
            <h2 className="font-display text-xl font-bold text-foreground">All Coverage</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {allCoverage.slice(0, visibleCount).map((article, i) => (
              <div key={article.id} className="animate-fade-in" style={{ animationDelay: `${(i % 18) * 50}ms` }}>
                <ArticleCard article={article} />
              </div>
            ))}
          </div>
          {visibleCount < allCoverage.length && (
            <div className="text-center mt-8">
              <Button onClick={handleShowMore} variant="outline" className="px-8">Show More</Button>
              <p className="text-sm text-muted-foreground mt-2">
                Showing {Math.min(visibleCount, allCoverage.length)} of {allCoverage.length} articles
              </p>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
      <AISearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
};

export default Index;
